<template>
  <!-- Teleport para renderizar el diálogo fuera de la jerarquía GWT -->
  <Teleport to="body">
    <div v-if="showDialog" class="expense-sheet-detail-dialog">
      <div class="detail-dialog-content">
        <!-- Header con título y botón volver -->
        <div class="detail-header fixNotchPaddingTop">
          <Button
            icon="pi pi-arrow-left"
            text
            rounded
            severity="secondary"
            @click="close()"
          />
          <span class="header-title">{{ data.name || "" }}</span>
        </div>

        <ExpenseSheetDetailSkeleton v-if="isLoading" />

        <!-- Contenido principal -->
        <div v-if="!isLoading" class="detail-content">
          <!-- Header Desktop/Mobile -->
          <div class="header-section">
            <ExpenseSheetDesktopHeader
              v-if="!isMobile"
              :data="data"
              :employeeId="employeeId"
              :hasItems="items.length > 0"
              @edit-expense-sheet="editExpenseSheet()"
              @confirm-send-expense-sheet="confirmSendExpenseSheet()"
              @cancel-expense-sheet-request="cancelExpenseSheetRequest()"
            />

            <ExpenseSheetMobileHeader
              v-else
              :data="data"
              :employeeId="employeeId"
              :hasItems="items.length > 0"
              @edit-expense-sheet="editExpenseSheet()"
              @confirm-send-expense-sheet="confirmSendExpenseSheet()"
              @cancel-expense-sheet-request="cancelExpenseSheetRequest()"
            />
          </div>

          <!-- Estado vacío -->
          <div v-if="items.length === 0" class="empty-state">
            <div class="empty-icon">
              <i class="pi pi-inbox" style="font-size: 4rem; color: grey"></i>
            </div>
            <span class="empty-text">{{
              t("wkt.client.expensesheets.detail-window.no_data")
            }}</span>
          </div>

          <!-- Lista de items Desktop/Mobile -->
          <div v-if="items.length > 0">
            <ExpenseSheetItemsTable
              v-if="!isMobile"
              :parent="data"
              :items="items"
              @download-document="downloadDocument($event)"
              @edit-item="editExpenseSheetItem($event)"
              @view-item="viewExpenseSheetItem($event)"
            />

            <ExpenseSheetItemCardList
              v-else
              :parent="data"
              :items="items"
              @download-document="downloadDocument($event)"
              @edit-item="editExpenseSheetItem($event)"
              @view-item="viewExpenseSheetItem($event)"
            />
          </div>
        </div>
      </div>

      <!-- Diálogo de edición de hoja de gastos -->
      <ExpenseSheetDialog
        ref="dlgEdit"
        @item-updated="onExpenseSheetUpdated"
        @item-deleted="onExpenseSheetDeleted"
      />

      <!-- Diálogo de confirmación de envío -->
      <ConfirmDialog
        ref="dlgConfirmSend"
        :title="t('wkt.client.expensesheets.confirm-send-dialog.title')"
        :message="t('wkt.client.expensesheets.confirm-send-dialog.text')"
        :titleOk="t('wkt.client.common.dialog-buttons.send')"
        :titleCancel="t('wkt.client.common.dialog-buttons.cancel')"
        @confirmed="sendExpenseSheet()"
      />

      <!-- Botón flotante para añadir nuevo gasto -->
      <Button
        v-if="!isLoading && [-1, 2].includes(Number(data.status))"
        icon="pi pi-plus"
        size="large"
        class="custom-add-button"
        :class="{ 'custom-add-button-mobile': isMobile }"
        rounded
        severity="var(--primary-color)"
        @click="addExpenseSheetItem()"
        v-tooltip="{ value: t('wkt.client.expensesheets.detail-window.add_expense_sheet_item') }"
      />

      <!-- Toast para mensajes -->
      <Toast position="bottom-center" />
    </div>
  </Teleport>

  <!-- Diálogo de item de hoja de gastos -->
  <ExpenseSheetItemDialog
    ref="dlgEditItem"
  />
</template>

<script setup lang="ts">
import expenseSheetsApi from "@wkt/api/expense-sheets";
import { gwtApi } from "@wkt/api/gwt/gwtAPI";
import { useToast } from "primevue/usetoast";
import { onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import ConfirmDialog from "../../common/ConfirmDialog.vue";
import ExpenseSheetDesktopHeader from "./ExpenseSheetDesktopHeader.vue";
import ExpenseSheetDialog from "./ExpenseSheetDialog.vue";
import ExpenseSheetItemCardList from "./ExpenseSheetItemCardList.vue";
import ExpenseSheetItemDialog from "./ExpenseSheetItemDialog.vue";
import ExpenseSheetItemsTable from "./ExpenseSheetItemsTable.vue";
import ExpenseSheetMobileHeader from "./ExpenseSheetMobileHeader.vue";
import ExpenseSheetDetailSkeleton from "@wkt/components/modules/skeletons/expensesheets/ExpenseSheetDetailSkeleton.vue";
import Toast from "primevue/toast";
import { useAlert } from "@wkt/composables/useAlert";

// Composables
const { t } = useI18n();
const toast = useToast();
const { showAlert } = useAlert();

// Emits
const emit = defineEmits<{
  refresh: [];
  closed: [];
  "send-success": [];
}>();

// State
const showDialog = ref(false);
const isLoading = ref(false);
const isMobile = ref(false);
const employeeId = ref<number | null>(null);
const data = ref<any>({});
const items = ref<any[]>([]);
const dlgEdit = ref<InstanceType<typeof ExpenseSheetDialog> | null>(null);
const dlgEditItem = ref<InstanceType<typeof ExpenseSheetItemDialog> | null>(
  null
);
const dlgConfirmSend = ref<InstanceType<typeof ConfirmDialog> | null>(null);

// Methods
function checkMobile() {
  isMobile.value = window.innerWidth < 768;
}

function onReveal(id: number) {
  open(id);
}

function open(id: number) {
  const sessionInfo = gwtApi.getSessionInformation();
  employeeId.value = sessionInfo?.employee?.id || null;

  showDialog.value = true;
  getFullExpenseSheetById(id);
}

function close() {
  showDialog.value = false;
  emit("closed");
}

function getFullExpenseSheetById(id: number) {
  isLoading.value = true;

  expenseSheetsApi.getFullExpenseSheetById(
    id,
    (response) => {
      isLoading.value = false;
      data.value = response;
      getExpenseSheetItemsBySheetId();
    },
    (err: any) => {
      isLoading.value = false;
      console.error("Error al cargar hoja de gastos:", err);
      showAlert({
        text: err.message || "Error al cargar hoja de gastos",
        color: "error",
      });
    }
  );
}

function getExpenseSheetItemsBySheetId() {
  isLoading.value = true;

  expenseSheetsApi.getExpenseSheetItemsBySheetId(
    { sheetId: data.value.id, sortBy: "id", sortDesc: true },
    (response) => {
      isLoading.value = false;
      items.value = response;
    },
    (err: any) => {
      isLoading.value = false;
      console.error("Error al cargar items:", err);
      showAlert({
        text: err.message || "Error al cargar items",
        color: "error",
      });
    }
  );
}

function editExpenseSheet() {
  if (dlgEdit.value) {
    dlgEdit.value.open(
      data.value.id,
      () => {
        getFullExpenseSheetById(data.value.id);
        emit("refresh");
      },
      () => {
        close();
        emit("refresh");
      }
    );
  }
}

function onExpenseSheetUpdated() {
  getFullExpenseSheetById(data.value.id);
  emit("refresh");
}

function onExpenseSheetDeleted() {
  close();
  emit("refresh");
}

function confirmSendExpenseSheet() {
  if (dlgConfirmSend.value) {
    dlgConfirmSend.value.confirm(data.value);
  }
}

function sendExpenseSheet() {
  isLoading.value = true;

  expenseSheetsApi.sendExpenseSheetById(
    data.value.id,
    (res) => {
      isLoading.value = false;

      if (res) {
        // Emitir evento al componente padre
        emit("send-success");

        close();
      } else {
        toast.add({
          severity: "error",
          summary: t("wkt.client.expensesheets.edit-dialog.send_error"),
          life: 3000,
        });
      }
    },
    (err: any) => {
      isLoading.value = false;
      console.error("Error al enviar hoja de gastos:", err);
      showAlert({
        text: err.message || "Error al enviar",
        color: "error",
      });
    }
  );
}

function cancelExpenseSheetRequest() {
  isLoading.value = true;

  expenseSheetsApi.cancelExpenseSheetRequestById(
    data.value.id,
    (res) => {
      isLoading.value = false;

      if (res) {
        getFullExpenseSheetById(data.value.id);
        emit("refresh");
        toast.add({
          severity: "success",
          summary: t("wkt.client.expensesheets.edit-dialog.cancel_request_ok"),
          life: 3000,
        });
      } else {
        toast.add({
          severity: "error",
          summary: t(
            "wkt.client.expensesheets.edit-dialog.cancel_request_error"
          ),
          life: 3000,
        });
      }
    },
    (err: any) => {
      isLoading.value = false;
      console.error("Error al cancelar solicitud:", err);
      showAlert({
        text: err.message || "Error al cancelar",
        color: "error",
      });
    }
  );
}

function addExpenseSheetItem() {
  if (dlgEditItem.value) {
    dlgEditItem.value.open(
      null,
      data.value.id,
      true,
      () => {
        getFullExpenseSheetById(data.value.id);
        emit("refresh");
      },
      () => {
        getFullExpenseSheetById(data.value.id);
        emit("refresh");
      }
    );
  }
}

function editExpenseSheetItem(item: any) {
  if (dlgEditItem.value) {
    dlgEditItem.value.open(
      item.id,
      data.value.id,
      true,
      () => {
        getFullExpenseSheetById(data.value.id);
        emit("refresh");
      },
      () => {
        getFullExpenseSheetById(data.value.id);
        emit("refresh");
      }
    );
  }
}

function viewExpenseSheetItem(item: any) {
  if (dlgEditItem.value) {
    dlgEditItem.value.open(
      item.id,
      data.value.id,
      false,
      () => {
        getFullExpenseSheetById(data.value.id);
        emit("refresh");
      },
      () => {
        getFullExpenseSheetById(data.value.id);
        emit("refresh");
      }
    );
  }
}

function downloadDocument(item: any) {
  gwtApi.downloadFile(
    `user/expense-sheet-item/${data.value.id}/download/${item.id}`,
    item.documentName
  );
}

// Lifecycle
onMounted(() => {
  checkMobile();
  window.addEventListener("resize", checkMobile);
});

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile);
});

// Exponer métodos para uso externo
defineExpose({
  open,
  onReveal,
  close,
});
</script>

<style scoped>
/* Dialog ocupa pantalla completa, GWT controla cuándo se muestra */
.expense-sheet-detail-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #ffffff;
  z-index: 1000;
}

.detail-dialog-content {
  height: 100%;
}

.detail-header {
  background-color: var(--primary-color);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: fixed;
  top: 0;
  width: 100%;
  height: 48px;
  z-index: 1001;
}

.detail-header :deep(.p-button) {
  color: white !important;
}

.header-title {
  color: white;
  font-size: 1.5em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mb-1 {
  margin-bottom: 0.25rem;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.mt-4 {
  margin-top: 1.5rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

.detail-content {
  padding: 1rem;
  position: relative;
  top: 3.5rem;
  overflow: auto;
}

.header-section {
  margin-bottom: 1.5rem;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  margin-top: 2rem;
}

.empty-icon {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow:
    0 2px 5px 0 rgba(0, 0, 0, 0.16),
    0 2px 10px 0 rgba(0, 0, 0, 0.12);
  margin-bottom: 1rem;
}

.empty-text {
  font-size: 16px;
  color: grey;
}

.add-item-button {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  background-color: #185ea9 !important;
}

.add-item-button-mobile {
  bottom: 80px;
}

.custom-table.p-datatable .p-datatable-table-container {
    height: calc(100vh - 365px);
}

/* Responsive */
@media (max-width: 768px) {
  .detail-header {
    position: relative;
    top: 0;
    width: 100%;
    z-index: 1001;
    height: auto;
    min-height: 60px;
  }
  .header-title {
    padding: 10px 0;
  }
  .detail-content {
    padding: 0.75rem;
    height: calc(100vh - 70px);
    padding-bottom: 20px;
    top: 10px;
  }
}
</style>
